import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useReincludeItemMutation } from "@/services/(company)/payrollRunsApi";

const REASON_MIN = 3;

/**
 * Put an excluded employee back into the run, with a reason (D3). The server
 * refuses one whose pay included overtime, a reimbursement or a one-time
 * deduction that the exclusion released, and says why.
 */
export const ReincludeDialog = ({
  runId,
  itemId,
  name,
  onClose,
}: {
  runId: number;
  itemId: number | null;
  name: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const [reinclude, { isLoading }] = useReincludeItemMutation();

  useEffect(() => {
    setReason("");
    setTried(false);
  }, [itemId]);

  const tooShort = reason.trim().length < REASON_MIN;

  const submit = async () => {
    setTried(true);
    if (tooShort || itemId === null) return;
    try {
      await reinclude({ runId, itemId, reason: reason.trim() }).unwrap();
      toast.success(
        t("company:payrolls.reincluded_toast", { name: name ?? "" }),
      );
      onClose();
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? t("company:payrolls.reinclude_failed")),
      );
    }
  };

  return (
    <Dialog open={itemId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("company:payrolls.reinclude_title")}</DialogTitle>
          <DialogDescription>
            {t("company:payrolls.reinclude_description", { name: name ?? "" })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-foreground">
            {t("company:payrolls.reinclude_reason")}
          </label>
          <textarea
            value={reason}
            rows={3}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            aria-invalid={tried && tooShort}
            className={
              tried && tooShort
                ? "resize-none rounded-xs border border-danger px-3 py-2 text-sm focus:outline-none"
                : "resize-none rounded-xs border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            }
          />
          {tried && tooShort && (
            <span className="text-[11px] text-danger">
              {t("company:payrolls.reinclude_reason_required")}
            </span>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("common:buttons.cancel")}
          </Button>
          <Button onClick={submit} isLoading={isLoading}>
            {t("company:payrolls.reinclude")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
